import { createSelector } from '@ngrx/store';
import { AppState } from '../app.state';
import { PortfolioItemsWithCalculation } from '@easy-lib/modules/portfolio/models/portfolio-item.model';
import { ISoldItemsCalculationTotalFields } from '@easy-lib/modules/portfolio';
import { soldItemsForExcelExportFields } from '@easy-lib/modules/sold-items/model/sold-items.model';

const getSoldItemsState = (state: AppState) => state.soldItemsState;
const getPortfolioMarketDataState = (state: AppState) => state.portfolioMarketDatastate.entities;

export const getSoldItemsLoading = createSelector(getSoldItemsState, state => state?.loading);
export const getLastSoldItemsInsertTime = createSelector(getSoldItemsState, state => state?.lastSoldInsertTime);

export const getPortfolioMarketData = createSelector(getPortfolioMarketDataState, state => Object.values(state));
export const getSoldItems = createSelector(getSoldItemsState, state => {
	return Object.values(state.entities).map(item => new PortfolioItemsWithCalculation({ ...item }));
});

export const getSoldItem = (symbolIsin: string) =>
	createSelector(getSoldItemsState, state => state.entities[symbolIsin]);

export const getPortfolioItemMarketData = (symbolIsin: string) =>
	createSelector(getPortfolioMarketDataState, state => state[symbolIsin]);

export const getSoldItemWithCalculation = (symbolIsin: string) =>
	createSelector(getSoldItem(symbolIsin), getPortfolioItemMarketData(symbolIsin), (pItem, marketData) => {
		return new PortfolioItemsWithCalculation({ ...pItem, ...marketData });
	});

export const getSoldItemsDPSList = createSelector(getSoldItems, soldItems => {
	return soldItems
		.filter(item => item.totalDPSAmount > 0 || item.lastDPSAmount > 0)
		.toSort((a, b) => b.lastDPSAmount - a.lastDPSAmount)
		.map(item => new PortfolioItemsWithCalculation({ ...item }));
});

export const getSoldItemsWithMarketData = createSelector(
	getSoldItems,
	getPortfolioMarketDataState,
	(soldItems, marketData) => {
		return soldItems.map(x => {
			const specificMarketData = marketData[x.symbolIsin] ?? {};
			return new PortfolioItemsWithCalculation({ ...x, ...specificMarketData });
		});
	}
);

export const getSoldItemsTotalValues = createSelector(getSoldItemsWithMarketData, soldItems => {
	const result: ISoldItemsCalculationTotalFields = {
		totalSoldProfit: soldItems.map(x => x.soldProfit).reduce((prev, current) => +prev + +current, 0),
		totalLastDPSAmount: soldItems.map(x => x.lastDPSAmount).reduce((prev, current) => +prev + +current, 0)
	};
	return result;
});

export const getSoldItemsForExcel = createSelector(getSoldItems, soldItems => {
	const headers = soldItemsForExcelExportFields.map(x => ({ title: x.farsiTitle }));
	const values = soldItems.map(data => {
		const resultData = {};
		soldItemsForExcelExportFields.forEach(info => {
			resultData[info.fieldName] = data[info.fieldName];
		});
		return resultData;
	});

	return { headers, values };
});
